import React, { useEffect, useState, useCallback } from 'react';
import {
  Users, CreditCard, TrendingUp, Activity, AlertTriangle,
  BarChart3, HelpCircle, Bot, Inbox, Shield, Clock, ArrowUpRight,
  Image, Zap, Bell, Server, Database, Cpu, HardDrive, Wifi, WifiOff,
  RefreshCw, CheckCircle2, XCircle, Globe, Sparkles,
} from 'lucide-react';
import { adminDb } from '../lib/adminDb';
import { adminFetch } from '../lib/api';
import { ChangelogModal } from '../components/ChangelogModal';
import { useAppVersion } from '../hooks/useAppVersion';

interface DashboardData {
  totalUsers: number;
  premiumUsers: number;
  newUsersToday: number;
  newUsersWeek: number;
  totalQuestions: number;
  questionsWithImages: number;
  chaptersCount: number;
  openTickets: number;
  aiMessagesToday: number;
  featureFlagsActive: number;
  auditEventsToday: number;
  securityScore: number;
  alerts: { type: 'danger' | 'warning' | 'info'; message: string; icon: React.ReactNode }[];
}

interface StatsData {
  users: { total: number; premium: number; newToday: number; new7d: number; conversionRate: number; trend: { date: string; count: number }[] };
  ai: { today: number; trend: { date: string; count: number }[] };
  revenue: { total: number; thisMonth: number; purchases: number };
  content: { questions: number; pendingFeedback: number };
}

interface SystemStatus {
  server: {
    startedAt: string;
    uptimeSeconds: number;
    port: number;
    environment: string;
    nodeVersion: string;
  };
  memory: {
    heapUsedMB: number;
    heapTotalMB: number;
    rssMB: number;
  };
  services: {
    supabase: string;
    ai: string;
  };
}

function formatUptime(totalSeconds: number): string {
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  parts.push(`${seconds}s`);
  return parts.join(' ');
}

const StatusDot: React.FC<{ status: 'ok' | 'warning' | 'error' }> = ({ status }) => {
  const colors = { ok: '#10B981', warning: '#F59E0B', error: '#EF4444' };
  return (
    <span style={{
      display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
      backgroundColor: colors[status],
      boxShadow: `0 0 6px ${colors[status]}80`,
      marginRight: 8,
    }} />
  );
};

const MarketingKPIs: React.FC = () => {
  const [kpis, setKpis] = useState<any>(null);

  useEffect(() => {
    (async () => {
      try {
        const resp = await adminFetch('/api/admin/marketing/dashboard/kpis');
        if (resp.ok) setKpis(await resp.json());
      } catch {}
    })();
  }, []);

  if (!kpis) return null;

  return (
    <div className="card" style={{ marginBottom: 'var(--space-6)', padding: 0, overflow: 'hidden' }}>
      <div style={{ padding: '10px 16px', background: 'linear-gradient(135deg, rgba(224,55,5,0.06), rgba(255,107,53,0.06))', borderBottom: '1px solid rgba(224,55,5,0.1)', display: 'flex', alignItems: 'center', gap: 8 }}>
        <Sparkles size={14} style={{ color: '#E03705' }} />
        <span style={{ fontSize: 13, fontWeight: 600, color: '#E03705' }}>Marketing OS</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0 }}>
        <div style={{ padding: '12px 16px', borderRight: '1px solid var(--border)' }}>
          <div className="text-xs text-muted">Zur Prüfung</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: kpis.pendingApprovals > 0 ? '#F59E0B' : 'var(--text-muted)' }}>{kpis.pendingApprovals}</div>
        </div>
        <div style={{ padding: '12px 16px', borderRight: '1px solid var(--border)' }}>
          <div className="text-xs text-muted">Geplant</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#8B5CF6' }}>{kpis.scheduledPosts}</div>
        </div>
        <div style={{ padding: '12px 16px', borderRight: '1px solid var(--border)' }}>
          <div className="text-xs text-muted">Gepostet (7d)</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#10B981' }}>{kpis.postedThisWeek}</div>
        </div>
        <div style={{ padding: '12px 16px' }}>
          <div className="text-xs text-muted">Aktive Kampagnen</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#3B82F6' }}>{kpis.activeCampaigns}</div>
        </div>
      </div>
    </div>
  );
};

export const DashboardPage: React.FC = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [systemStatus, setSystemStatus] = useState<SystemStatus | null>(null);
  const [systemLoading, setSystemLoading] = useState(true);
  const [liveUptime, setLiveUptime] = useState(0);
  const [showChangelog, setShowChangelog] = useState(false);
  const [stats, setStats] = useState<StatsData | null>(null);
  const appVersion = useAppVersion();

  useEffect(() => { loadDashboardData(); loadStats(); }, []);

  // Fetch system status
  const loadSystemStatus = useCallback(async () => {
    try {
      setSystemLoading(true);
      const resp = await adminFetch('/api/system-status');
      if (resp.ok) {
        const status = await resp.json();
        setSystemStatus(status);
        setLiveUptime(status.server.uptimeSeconds);
      }
    } catch (err) {
      console.error('System status fetch failed:', err);
    } finally {
      setSystemLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSystemStatus();
    const interval = setInterval(loadSystemStatus, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, [loadSystemStatus]);

  // Live uptime ticker
  useEffect(() => {
    if (!systemStatus) return;
    const interval = setInterval(() => {
      setLiveUptime(prev => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [systemStatus]);

  const loadDashboardData = async () => {
    try {
      const [usersRes, questionsRes, questionsImgRes, ticketsRes, flagsRes, auditRes] = await Promise.all([
        adminDb.from('users').select('data, created_at'),
        adminDb.from('questions').select('id', { count: 'exact', head: true }),
        adminDb.rpc('get_questions_with_images'),
        adminDb.from('support_tickets').select('id', { count: 'exact', head: true }).eq('status', 'open'),
        adminDb.from('feature_flags').select('id', { count: 'exact', head: true }).eq('enabled', true),
        adminDb.from('audit_logs').select('id', { count: 'exact', head: true }),
      ]);

      const users = usersRes.data || [];
      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const weekAgo = new Date(todayStart.getTime() - 7 * 24 * 60 * 60 * 1000);

      const premiumUsers = users.filter(u => u.data?.isPremium).length;
      const newUsersToday = users.filter(u => new Date(u.created_at) >= todayStart).length;
      const newUsersWeek = users.filter(u => new Date(u.created_at) >= weekAgo).length;

      let aiMessagesToday = 0;
      users.forEach(u => {
        const chat = u.data?.chatHistory;
        if (Array.isArray(chat)) {
          chat.forEach((msg: any) => {
            if (msg?.timestamp && new Date(msg.timestamp) >= todayStart) aiMessagesToday++;
          });
        }
      });

      const totalQuestions = questionsRes.count || 0;
      const questionsWithImages = (questionsImgRes.data || []).length;
      const openTickets = ticketsRes.count || 0;
      const featureFlagsActive = flagsRes.count || 0;

      const alerts: DashboardData['alerts'] = [];
      if (openTickets > 0) alerts.push({ type: 'warning', message: `${openTickets} offene Support-Tickets warten auf Bearbeitung.`, icon: <Inbox size={16} /> });
      if (questionsWithImages < totalQuestions) alerts.push({ type: 'warning', message: `${totalQuestions - questionsWithImages} Fragen ohne Bild — im Bild-Manager generieren.`, icon: <Image size={16} /> });
      if (premiumUsers === 0 && users.length > 5) alerts.push({ type: 'info', message: 'Noch keine Premium-Nutzer. CopeCart-Webhook prüfen.', icon: <CreditCard size={16} /> });
      if (users.length === 0) alerts.push({ type: 'info', message: 'Noch keine Nutzer registriert. App-Launch steht bevor.', icon: <Users size={16} /> });
      if (alerts.length === 0) alerts.push({ type: 'info', message: 'Alles läuft reibungslos. Keine Warnungen.', icon: <Activity size={16} /> });

      setData({
        totalUsers: users.length, premiumUsers, newUsersToday, newUsersWeek,
        totalQuestions, questionsWithImages, chaptersCount: 14,
        openTickets, aiMessagesToday, featureFlagsActive,
        auditEventsToday: auditRes.count || 0,
        securityScore: 82,
        alerts,
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const resp = await adminFetch('/api/admin/stats');
      if (resp.ok) setStats(await resp.json());
    } catch (err) {
      console.error('Stats fetch failed:', err);
    }
  };

  if (loading || !data) {
    return (
      <div>
        <div className="page-header"><h1>Kommandozentrale</h1></div>
        <div className="grid-cols-3" style={{ marginBottom: 'var(--space-6)' }}>
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="kpi-card">
              <div className="skeleton" style={{ width: 100, height: 14 }} />
              <div className="skeleton" style={{ width: 60, height: 32, marginTop: 8 }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const conversionRate = data.totalUsers > 0 ? ((data.premiumUsers / data.totalUsers) * 100).toFixed(1) : '0';
  const memoryPercent = systemStatus ? Math.round((systemStatus.memory.heapUsedMB / systemStatus.memory.heapTotalMB) * 100) : 0;

  return (
    <div>
      <ChangelogModal isOpen={showChangelog} onClose={() => setShowChangelog(false)} />
      <div className="page-header">
        <div className="flex items-center gap-3">
          <h1>Kommandozentrale</h1>
          <span 
            onClick={() => setShowChangelog(true)}
            style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6, background: 'rgba(59,130,246,0.12)', color: '#3B82F6', letterSpacing: '0.03em', cursor: 'pointer', transition: 'all 0.15s' }}
            onMouseEnter={e => { (e.target as HTMLElement).style.background = 'rgba(59,130,246,0.25)'; }}
            onMouseLeave={e => { (e.target as HTMLElement).style.background = 'rgba(59,130,246,0.12)'; }}
            title="Changelog anzeigen"
          >v{appVersion}</span>
        </div>
        <span className="text-xs text-muted">Letzte Aktualisierung: {new Date().toLocaleString('de-DE')}</span>
      </div>

      {/* Primary KPIs */}
      <div className="grid-cols-4" style={{ marginBottom: 'var(--space-6)' }}>
        <div className="kpi-card">
          <div className="flex items-center justify-between">
            <span className="kpi-label">Nutzer gesamt</span>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(59,130,246,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--info)' }}><Users size={16} /></div>
          </div>
          <div className="kpi-value">{data.totalUsers}</div>
          {data.newUsersWeek > 0 && <div className="text-xs" style={{ color: 'var(--success)' }}><ArrowUpRight size={12} /> +{data.newUsersWeek} diese Woche</div>}
        </div>
        <div className="kpi-card">
          <div className="flex items-center justify-between">
            <span className="kpi-label">Premium-Nutzer</span>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(16,185,129,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--success)' }}><CreditCard size={16} /></div>
          </div>
          <div className="kpi-value" style={{ color: 'var(--success)' }}>{data.premiumUsers}</div>
          <div className="text-xs text-muted">Conversion: {conversionRate}%</div>
        </div>
        <div className="kpi-card">
          <div className="flex items-center justify-between">
            <span className="kpi-label">Fragen gesamt</span>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(139,92,246,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8B5CF6' }}><HelpCircle size={16} /></div>
          </div>
          <div className="kpi-value">{data.totalQuestions}</div>
          <div className="text-xs text-muted">{data.questionsWithImages} mit Bild ({data.totalQuestions > 0 ? Math.round((data.questionsWithImages / data.totalQuestions) * 100) : 0}%)</div>
        </div>
        <div className="kpi-card">
          <div className="flex items-center justify-between">
            <span className="kpi-label">KI-Nachrichten heute</span>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(236,72,153,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#EC4899' }}><Bot size={16} /></div>
          </div>
          <div className="kpi-value">{data.aiMessagesToday}</div>
          <div className="text-xs text-muted">Neue heute: +{data.newUsersToday}</div>
        </div>
      </div>

      {/* Secondary KPIs */}
      <div className="grid-cols-4" style={{ marginBottom: 'var(--space-6)' }}>
        <div className="kpi-card">
          <span className="kpi-label">Offene Tickets</span>
          <span className="kpi-value" style={{ color: data.openTickets > 0 ? 'var(--warning)' : 'var(--success)' }}>{data.openTickets}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Feature Flags aktiv</span>
          <span className="kpi-value">{data.featureFlagsActive}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Kapitel</span>
          <span className="kpi-value">{data.chaptersCount}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Security Score</span>
          <span className="kpi-value" style={{ color: data.securityScore >= 80 ? 'var(--success)' : 'var(--warning)' }}>{data.securityScore}%</span>
        </div>
      </div>

      {/* Marketing KPIs */}
      <MarketingKPIs />

      {/* Alerts */}
      <div className="card" style={{ marginBottom: 'var(--space-6)' }}>
        <h3 style={{ marginBottom: 'var(--space-4)' }}><Bell size={18} /> Alerts & Hinweise</h3>
        <div className="flex flex-col gap-3">
          {data.alerts.map((alert, i) => (
            <div key={i} className={`alert alert-${alert.type}`}>
              {alert.icon}
              <span>{alert.message}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Trend Charts */}
      {stats && (
        <div className="grid-cols-2" style={{ marginBottom: 'var(--space-6)' }}>
          {/* User Registration Trend */}
          <div className="card">
            <div className="flex items-center justify-between" style={{ marginBottom: 'var(--space-4)' }}>
              <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}><Users size={16} /> Neue Nutzer (7 Tage)</h3>
              <span style={{ fontSize: 20, fontWeight: 700, color: 'var(--info)' }}>+{stats.users.new7d}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 80 }}>
              {stats.users.trend.map((d, i) => {
                const maxVal = Math.max(...stats.users.trend.map(t => t.count), 1);
                const h = Math.max((d.count / maxVal) * 70, 4);
                const dayName = new Date(d.date + 'T12:00:00').toLocaleDateString('de-DE', { weekday: 'short' });
                return (
                  <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)' }}>{d.count || ''}</span>
                    <div style={{ width: '100%', height: h, borderRadius: 4, background: 'linear-gradient(180deg, #3B82F6 0%, #1D4ED8 100%)', transition: 'height 0.5s ease' }} />
                    <span style={{ fontSize: 9, color: 'var(--text-muted)' }}>{dayName}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* AI Usage Trend */}
          <div className="card">
            <div className="flex items-center justify-between" style={{ marginBottom: 'var(--space-4)' }}>
              <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}><Bot size={16} /> KI-Nutzung (7 Tage)</h3>
              <span style={{ fontSize: 20, fontWeight: 700, color: '#EC4899' }}>{stats.ai.today} heute</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 80 }}>
              {stats.ai.trend.map((d, i) => {
                const maxVal = Math.max(...stats.ai.trend.map(t => t.count), 1);
                const h = Math.max((d.count / maxVal) * 70, 4);
                const dayName = new Date(d.date + 'T12:00:00').toLocaleDateString('de-DE', { weekday: 'short' });
                return (
                  <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)' }}>{d.count || ''}</span>
                    <div style={{ width: '100%', height: h, borderRadius: 4, background: 'linear-gradient(180deg, #EC4899 0%, #BE185D 100%)', transition: 'height 0.5s ease' }} />
                    <span style={{ fontSize: 9, color: 'var(--text-muted)' }}>{dayName}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Revenue + Content Stats */}
      {stats && (
        <div className="grid-cols-3" style={{ marginBottom: 'var(--space-6)' }}>
          <div className="kpi-card">
            <div className="flex items-center justify-between">
              <span className="kpi-label">Umsatz (gesamt)</span>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(16,185,129,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--success)' }}><CreditCard size={16} /></div>
            </div>
            <div className="kpi-value" style={{ color: 'var(--success)' }}>{stats.revenue.total.toFixed(2)} €</div>
            <div className="text-xs text-muted">Diesen Monat: {stats.revenue.thisMonth.toFixed(2)} € · {stats.revenue.purchases} Käufe</div>
          </div>
          <div className="kpi-card">
            <div className="flex items-center justify-between">
              <span className="kpi-label">Conversion Rate</span>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(245,158,11,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#F59E0B' }}><TrendingUp size={16} /></div>
            </div>
            <div className="kpi-value" style={{ color: stats.users.conversionRate > 5 ? 'var(--success)' : '#F59E0B' }}>{stats.users.conversionRate}%</div>
            <div className="text-xs text-muted">{stats.users.premium} von {stats.users.total} Nutzern</div>
          </div>
          <div className="kpi-card">
            <div className="flex items-center justify-between">
              <span className="kpi-label">Offenes Feedback</span>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(239,68,68,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#EF4444' }}><AlertTriangle size={16} /></div>
            </div>
            <div className="kpi-value" style={{ color: stats.content.pendingFeedback > 0 ? '#EF4444' : 'var(--success)' }}>{stats.content.pendingFeedback}</div>
            <div className="text-xs text-muted">Bild-Meldungen warten</div>
          </div>
        </div>
      )}

      {/* System Status */}
      <div className="card" style={{ marginBottom: 'var(--space-6)' }}>
        <div className="flex items-center justify-between" style={{ marginBottom: 'var(--space-4)' }}>
          <h3 style={{ margin: 0 }}><Server size={18} /> System-Status</h3>
          <button
            onClick={loadSystemStatus}
            className="btn btn-secondary"
            style={{ padding: '4px 12px', fontSize: 12 }}
            disabled={systemLoading}
          >
            <RefreshCw size={14} style={systemLoading ? { animation: 'spin 1s linear infinite' } : {}} /> Aktualisieren
          </button>
        </div>

        {systemLoading && !systemStatus ? (
          <div className="flex gap-4" style={{ padding: 'var(--space-4)' }}>
            {[1, 2, 3].map(i => (
              <div key={i} style={{ flex: 1 }}>
                <div className="skeleton" style={{ width: '80%', height: 14, marginBottom: 8 }} />
                <div className="skeleton" style={{ width: '60%', height: 24 }} />
              </div>
            ))}
          </div>
        ) : systemStatus ? (
          <div>
            {/* Server Info Grid */}
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'var(--space-4)',
              marginBottom: 'var(--space-5)',
            }}>
              {/* Uptime */}
              <div style={{
                padding: 'var(--space-4)', borderRadius: 12,
                background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.15)',
              }}>
                <div className="flex items-center gap-2" style={{ marginBottom: 8 }}>
                  <Clock size={14} style={{ color: '#10B981' }} />
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Uptime</span>
                </div>
                <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>
                  {formatUptime(liveUptime)}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                  Gestartet: {new Date(systemStatus.server.startedAt).toLocaleString('de-DE')}
                </div>
              </div>

              {/* Environment */}
              <div style={{
                padding: 'var(--space-4)', borderRadius: 12,
                background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.15)',
              }}>
                <div className="flex items-center gap-2" style={{ marginBottom: 8 }}>
                  <Globe size={14} style={{ color: '#3B82F6' }} />
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Umgebung</span>
                </div>
                <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>
                  <span style={{
                    display: 'inline-block', padding: '2px 10px', borderRadius: 6,
                    fontSize: 13, fontWeight: 600,
                    background: systemStatus.server.environment === 'production' ? 'rgba(239,68,68,0.12)' : 'rgba(59,130,246,0.12)',
                    color: systemStatus.server.environment === 'production' ? '#EF4444' : '#3B82F6',
                  }}>
                    {systemStatus.server.environment === 'production' ? '🔴 Production' : '🔵 Development'}
                  </span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                  Port: {systemStatus.server.port}
                </div>
              </div>

              {/* Node.js */}
              <div style={{
                padding: 'var(--space-4)', borderRadius: 12,
                background: 'rgba(139,92,246,0.06)', border: '1px solid rgba(139,92,246,0.15)',
              }}>
                <div className="flex items-center gap-2" style={{ marginBottom: 8 }}>
                  <Cpu size={14} style={{ color: '#8B5CF6' }} />
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Node.js</span>
                </div>
                <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)' }}>
                  {systemStatus.server.nodeVersion}
                </div>
              </div>

              {/* Memory */}
              <div style={{
                padding: 'var(--space-4)', borderRadius: 12,
                background: memoryPercent > 80 ? 'rgba(239,68,68,0.06)' : 'rgba(245,158,11,0.06)',
                border: `1px solid ${memoryPercent > 80 ? 'rgba(239,68,68,0.15)' : 'rgba(245,158,11,0.15)'}`,
              }}>
                <div className="flex items-center gap-2" style={{ marginBottom: 8 }}>
                  <HardDrive size={14} style={{ color: memoryPercent > 80 ? '#EF4444' : '#F59E0B' }} />
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Speicher (Heap)</span>
                </div>
                <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)' }}>
                  {systemStatus.memory.heapUsedMB} MB
                </div>
                <div style={{
                  marginTop: 6, height: 6, borderRadius: 3,
                  background: 'rgba(255,255,255,0.1)', overflow: 'hidden',
                }}>
                  <div style={{
                    height: '100%', borderRadius: 3, transition: 'width 0.5s ease',
                    width: `${memoryPercent}%`,
                    background: memoryPercent > 80 ? '#EF4444' : memoryPercent > 60 ? '#F59E0B' : '#10B981',
                  }} />
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                  {memoryPercent}% von {systemStatus.memory.heapTotalMB} MB · RSS: {systemStatus.memory.rssMB} MB
                </div>
              </div>
            </div>

            {/* Services Status */}
            <div style={{
              padding: 'var(--space-4)', borderRadius: 12,
              background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
            }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>
                Service-Verbindungen
              </div>
              <div className="flex gap-6">
                <div className="flex items-center gap-2">
                  <StatusDot status={systemStatus.services.supabase === 'verbunden' ? 'ok' : systemStatus.services.supabase === 'Fehler' ? 'error' : 'warning'} />
                  <Database size={14} />
                  <span style={{ fontSize: 13 }}>Supabase: </span>
                  <span style={{
                    fontSize: 13, fontWeight: 600,
                    color: systemStatus.services.supabase === 'verbunden' ? '#10B981' : systemStatus.services.supabase === 'Fehler' ? '#EF4444' : '#F59E0B',
                  }}>
                    {systemStatus.services.supabase}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <StatusDot status={systemStatus.services.ai.includes('konfiguriert') || systemStatus.services.ai === 'Vertex AI' ? 'ok' : 'warning'} />
                  <Bot size={14} />
                  <span style={{ fontSize: 13 }}>KI-Service: </span>
                  <span style={{
                    fontSize: 13, fontWeight: 600,
                    color: systemStatus.services.ai.includes('konfiguriert') || systemStatus.services.ai === 'Vertex AI' ? '#10B981' : '#F59E0B',
                  }}>
                    {systemStatus.services.ai}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="alert alert-warning">
            <WifiOff size={16} />
            <span>System-Status konnte nicht abgerufen werden. Stellen Sie sicher, dass der Server läuft.</span>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="card">
        <h3 style={{ marginBottom: 'var(--space-4)' }}><Zap size={18} /> Quick Actions</h3>
        <div className="flex gap-3 flex-wrap">
          <a href="/questions" className="btn btn-primary"><HelpCircle size={16} /> Fragen verwalten</a>
          <a href="/images" className="btn btn-secondary"><Image size={16} /> Bilder generieren</a>
          <a href="/users" className="btn btn-secondary"><Users size={16} /> Nutzer anzeigen</a>
          <a href="/support" className="btn btn-secondary"><Inbox size={16} /> Support-Tickets</a>
          <a href="/churn" className="btn btn-secondary"><TrendingUp size={16} /> Churn-Analyse</a>
          <a href="/reports" className="btn btn-secondary"><BarChart3 size={16} /> Reports</a>
        </div>
      </div>

      {/* CSS for spinner animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};
